<template>
  <section class="readiness-panel" aria-labelledby="travel-readiness-title">
    <div class="readiness-header">
      <div>
        <p class="readiness-kicker">旅の準備</p>
        <h2 id="travel-readiness-title" class="readiness-title">
          準備状況 {{ readinessPercent }}%
        </h2>
      </div>
      <span :class="['readiness-badge', readinessLevel.className]">
        {{ readinessLevel.label }}
      </span>
    </div>

    <div class="readiness-meter" aria-hidden="true">
      <span :style="{ width: `${readinessPercent}%` }"></span>
    </div>

    <div class="readiness-sections">
      <div
        v-for="section in sections"
        :key="section.key"
        class="readiness-section"
      >
        <div class="section-main">
          <span class="section-icon">
            <IconSvg :name="section.icon" size="sm" />
          </span>
          <span class="section-copy">
            <span class="section-label">{{ section.label }}</span>
            <strong>{{ section.status }}</strong>
            <span class="section-note">{{ section.note }}</span>
          </span>
        </div>
        <button
          class="section-action"
          type="button"
          @click="$emit('open-tab', section.tab)"
        >
          開く
        </button>
      </div>
    </div>
  </section>
</template>

<script>
import { computed } from "vue";
import IconSvg from "@/components/IconSvg.vue";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function isComplete(value) {
  return String(value || "0") === "1";
}

function toDateOnly(value) {
  if (!value) return null;
  const date = new Date(String(value).slice(0, 10));
  if (Number.isNaN(date.getTime())) return null;
  date.setHours(0, 0, 0, 0);
  return date;
}

function daysUntil(value, today) {
  const date = toDateOnly(value);
  if (!date) return null;
  return Math.round((date.getTime() - today.getTime()) / MS_PER_DAY);
}

export default {
  name: "TravelReadinessPanel",
  components: { IconSvg },
  props: {
    schedules: {
      type: Array,
      default: () => [],
    },
    checklistItems: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["open-tab"],
  setup(props) {
    const today = computed(() => {
      const value = new Date();
      value.setHours(0, 0, 0, 0);
      return value;
    });

    const pendingReservations = computed(() =>
      props.schedules.filter(
        (item) =>
          item.RESERVATION_FLG === "1" && item.RESERVATION_DONE_FLG !== "1",
      ),
    );

    const todoItems = computed(() =>
      props.checklistItems.filter(
        (item) => (item.ITEM_TYPE || "TODO") === "TODO",
      ),
    );

    const incompleteTodoItems = computed(() =>
      todoItems.value.filter((item) => !isComplete(item.COMPLETED_FLG)),
    );

    const overdueTodos = computed(() =>
      incompleteTodoItems.value.filter((item) => {
        const delta = daysUntil(item.DEADLINE, today.value);
        return delta !== null && delta < 0;
      }),
    );

    const dueSoonTodos = computed(() =>
      incompleteTodoItems.value.filter((item) => {
        const delta = daysUntil(item.DEADLINE, today.value);
        return delta !== null && delta >= 0 && delta <= 3;
      }),
    );

    const belongingItems = computed(() =>
      props.checklistItems.filter((item) => item.ITEM_TYPE === "BELONGINGS"),
    );

    const incompleteBelongings = computed(() =>
      belongingItems.value.filter((item) => {
        if (!isComplete(item.COMPLETED_FLG)) return true;
        return (item.MEMBERS || []).some(
          (member) => !isComplete(member.COMPLETED_FLG),
        );
      }),
    );

    const incompleteBelongingMemberCount = computed(() =>
      belongingItems.value.reduce(
        (total, item) =>
          total +
          (item.MEMBERS || []).filter(
            (member) => !isComplete(member.COMPLETED_FLG),
          ).length,
        0,
      ),
    );

    const riskCount = computed(
      () =>
        pendingReservations.value.length +
        incompleteTodoItems.value.length +
        incompleteBelongings.value.length,
    );

    const totalTrackedCount = computed(
      () =>
        props.schedules.filter((item) => item.RESERVATION_FLG === "1").length +
        todoItems.value.length +
        belongingItems.value.length,
    );

    const readinessPercent = computed(() => {
      if (totalTrackedCount.value === 0) return 100;
      const done = Math.max(totalTrackedCount.value - riskCount.value, 0);
      return Math.round((done / totalTrackedCount.value) * 100);
    });

    const readinessLevel = computed(() => {
      if (riskCount.value === 0) {
        return { label: "順調", className: "is-good" };
      }
      if (overdueTodos.value.length > 0 || pendingReservations.value.length > 0) {
        return { label: "要確認", className: "is-risk" };
      }
      return { label: "確認中", className: "is-watch" };
    });

    const sections = computed(() => [
      {
        key: "reservations",
        tab: "schedule",
        label: "Reservations",
        icon: "calendar-solid-full",
        status:
          pendingReservations.value.length > 0
            ? `未予約 ${pendingReservations.value.length}件`
            : "予約OK",
        note:
          pendingReservations.value.length > 0
            ? "予約フラグあり・未完了"
            : "未完了の予約予定なし",
      },
      {
        key: "todo",
        tab: "todo",
        label: "To Do",
        icon: "triangle-exclamation-solid-full",
        status:
          incompleteTodoItems.value.length > 0
            ? `未完了 ${incompleteTodoItems.value.length}件`
            : "To Do OK",
        note: `期限切れ ${overdueTodos.value.length}件 / 3日以内 ${dueSoonTodos.value.length}件`,
      },
      {
        key: "belongings",
        tab: "belongings",
        label: "Belongings",
        icon: "bag-shopping-solid-full",
        status:
          incompleteBelongings.value.length > 0
            ? `未完了 ${incompleteBelongings.value.length}件`
            : "持ち物OK",
        note:
          incompleteBelongingMemberCount.value > 0
            ? `メンバー未完了 ${incompleteBelongingMemberCount.value}件`
            : "個別完了も確認済み",
      },
    ]);

    return {
      readinessPercent,
      readinessLevel,
      sections,
    };
  },
};
</script>

<style scoped>
.readiness-panel {
  margin-top: 1rem;
  overflow: hidden;
  background: #ffffff;
  border: 1px solid var(--color-border-light);
  border-radius: 8px;
  box-shadow: var(--shadow-sm);
}

.readiness-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.25rem 0.75rem;
}

.readiness-kicker {
  margin: 0 0 0.25rem;
  color: var(--primary-hover);
  font-size: 0.75rem;
  font-weight: 700;
}

.readiness-title {
  margin: 0;
  color: var(--color-text);
  font-size: 1.125rem;
  font-weight: 800;
}

.readiness-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 2rem;
  padding: 0 0.625rem;
  border-radius: 999px;
  font-size: 0.8125rem;
  font-weight: 800;
  white-space: nowrap;
}

.readiness-badge.is-good {
  color: #166534;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
}

.readiness-badge.is-watch {
  color: #854d0e;
  background: #fefce8;
  border: 1px solid #fde68a;
}

.readiness-badge.is-risk {
  color: #b91c1c;
  background: #fef2f2;
  border: 1px solid #fecaca;
}

.readiness-meter {
  height: 0.5rem;
  margin: 0 1.25rem 1rem;
  overflow: hidden;
  background: #f3f4f6;
  border-radius: 999px;
}

.readiness-meter span {
  display: block;
  height: 100%;
  background: linear-gradient(90deg, #16a34a, #2563eb);
  border-radius: inherit;
}

.readiness-sections {
  display: grid;
  gap: 0.75rem;
  padding: 0 1.25rem 1.25rem;
}

.readiness-section {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem;
  background: #f9fafb;
  border: 1px solid var(--color-border-light);
  border-radius: 8px;
}

.section-main {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
}

.section-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  flex-shrink: 0;
  color: var(--primary-hover);
  background: #ffffff;
  border: 1px solid var(--color-border-light);
  border-radius: 8px;
}

.section-copy {
  display: grid;
  gap: 0.125rem;
  min-width: 0;
}

.section-label,
.section-note {
  overflow: hidden;
  color: var(--color-text-muted);
  font-size: 0.75rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.section-copy strong {
  color: var(--color-text);
  font-size: 0.9375rem;
}

.section-action {
  min-height: 2.25rem;
  padding: 0 0.875rem;
  color: #ffffff;
  background: var(--primary-color);
  border: 0;
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 700;
  cursor: pointer;
}

.section-action:hover {
  background: var(--primary-hover);
}

@media (min-width: 720px) {
  .readiness-sections {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .readiness-section {
    grid-template-columns: 1fr;
    align-content: space-between;
  }

  .section-action {
    width: 100%;
  }
}
</style>
